<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue';
import api from "@/services/api.js";
import logo from "@/assets/img/fondotam-blanco.png";
import Swal from 'sweetalert2';
import { getRol, ROLES } from "@/utils/roles.js";

// --- ESTADOS ---
const activos = ref([]);
const regionales = ref([]);
const gerencias = ref([]);
const secciones = ref([]);
const gruposContables = ref([]);
const tiposActivos = ref([]);
const cargando = ref(false);

const filtros = reactive({
  search: '',
  regional: '',
  gerencia: '',
  seccion: '',
  grupo_contable_seleccionado: '',
  tipo_activo: '',
  situacion_activo: '',
  estado_actual: '',
  tiene_tamep: '',
  tiene_tab: ''
});

// --- ESTADO DE PAGINACIÓN ---
const paginacion = reactive({
  paginaActual: 1,
  itemsPorPagina: 10
});

// --- PERMISOS ---
const rolUsuario = getRol();
const puedeEliminar = computed(() => {
  return rolUsuario === ROLES.ADMIN_INFORMATICO || rolUsuario === ROLES.ADMIN_PRINCIPAL;
});

// --- LÓGICA DE DATOS Y PARAMETROS ---
const cargarParametrosIniciales = async () => {
  try {
    const [resReg, resGrupos] = await Promise.all([
      api.get("regionales/"),
      api.get("grupos-contables/")
    ]);
    regionales.value = resReg.data.results || resReg.data;
    gruposContables.value = resGrupos.data.results || resGrupos.data;
  } catch (e) {
    console.error("Error cargando parámetros iniciales", e);
  }
};

// --- CASCADAS DE SELECCIÓN ---
watch(() => filtros.regional, async (newVal) => {
  gerencias.value = [];
  secciones.value = [];
  filtros.gerencia = '';
  filtros.seccion = '';
  if (newVal) {
    try {
      const res = await api.get(`gerencias/?regional=${newVal}`);
      gerencias.value = res.data.results || res.data;
    } catch (e) {
      console.error("Error cargando gerencias", e);
    }
  }
  realizarBusqueda();
});

watch(() => filtros.gerencia, async (newVal) => {
  secciones.value = [];
  filtros.seccion = '';
  if (newVal) {
    try {
      const res = await api.get(`secciones/?gerencia=${newVal}`);
      secciones.value = res.data.results || res.data;
    } catch (e) {
      console.error("Error cargando secciones", e);
    }
  }
  realizarBusqueda();
});

watch(() => filtros.seccion, () => {
  realizarBusqueda();
});

watch(() => filtros.grupo_contable_seleccionado, async (nuevoGrupo) => {
  tiposActivos.value = [];
  filtros.tipo_activo = '';
  if (nuevoGrupo) {
    try {
      const res = await api.get(`tipos-activos/?grupo_contable=${nuevoGrupo}`);
      tiposActivos.value = res.data.results || res.data;
    } catch (e) {
      console.error("Error al cargar tipos", e);
    }
  }
  realizarBusqueda();
});

const realizarBusqueda = async () => {
  cargando.value = true;
  paginacion.paginaActual = 1;
  try {
    const params = new URLSearchParams();
    if (filtros.search) params.append('search', filtros.search);
    if (filtros.regional) params.append('regional', filtros.regional);
    if (filtros.gerencia) params.append('gerencia', filtros.gerencia);
    if (filtros.seccion) params.append('seccion', filtros.seccion);
    if (filtros.grupo_contable_seleccionado) params.append('grupo_contable_seleccionado', filtros.grupo_contable_seleccionado);
    if (filtros.tipo_activo) params.append('tipo_activo', filtros.tipo_activo);
    if (filtros.situacion_activo) params.append('situacion_activo', filtros.situacion_activo);
    if (filtros.estado_actual) params.append('estado_actual', filtros.estado_actual);
    if (filtros.tiene_tamep !== '') params.append('tiene_tamep', filtros.tiene_tamep);
    if (filtros.tiene_tab !== '') params.append('tiene_tab', filtros.tiene_tab);

    const response = await api.get(`gestion-activos/?${params.toString()}`);
    activos.value = response.data.results || response.data;
  } catch (error) {
    console.error("Error en la consulta profesional", error);
  } finally {
    cargando.value = false;
  }
};

const limpiarFiltros = () => {
  filtros.search = '';
  filtros.regional = '';
  filtros.gerencia = '';
  filtros.seccion = '';
  filtros.grupo_contable_seleccionado = '';
  filtros.tipo_activo = '';
  filtros.situacion_activo = '';
  filtros.estado_actual = '';
  filtros.tiene_tamep = '';
  filtros.tiene_tab = '';
  gerencias.value = [];
  secciones.value = [];
  tiposActivos.value = [];
  realizarBusqueda();
};



// --- LÓGICA DE ELIMINACIÓN ---
const eliminarActivo = async (id, codigo) => {
  const result = await Swal.fire({
    title: '¿Está seguro?',
    text: `Se eliminará permanentemente el activo ${codigo || 'S/N'}. Esta acción quedará registrada en el sistema de auditoría.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: '<i class="fas fa-trash me-2"></i>Sí, Eliminar',
    cancelButtonText: 'Cancelar'
  });

  if (result.isConfirmed) {
    try {
      await api.delete(`gestion-activos/${id}/`);
      Swal.fire({
        title: 'Eliminado!',
        text: 'El registro ha sido borrado y auditado exitosamente.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
      realizarBusqueda();
    } catch (error) {
      console.error('Error al eliminar', error);
      Swal.fire('Error', 'No se pudo eliminar el activo. Es probable que no tenga los permisos suficientes.', 'error');
    }
  }
};

onMounted(() => {
  cargarParametrosIniciales();
  realizarBusqueda();
});

// --- LÓGICA COMPUTADA PARA PAGINACIÓN INTELIGENTE ---
const totalPaginas = computed(() => {
  return Math.ceil(activos.value.length / paginacion.itemsPorPagina) || 1;
});

const paginasVisibles = computed(() => {
  const maxVisibles = 5;
  const total = totalPaginas.value;
  const actual = paginacion.paginaActual;

  let inicio = Math.max(1, actual - Math.floor(maxVisibles / 2));
  let fin = Math.min(total, inicio + maxVisibles - 1);

  if (fin - inicio + 1 < maxVisibles) {
    inicio = Math.max(1, fin - maxVisibles + 1);
  }

  const paginas = [];
  for (let i = inicio; i <= fin; i++) {
    paginas.push(i);
  }
  return paginas;
});

const activosPaginados = computed(() => {
  const inicio = (paginacion.paginaActual - 1) * paginacion.itemsPorPagina;
  const fin = inicio + paginacion.itemsPorPagina;
  return activos.value.slice(inicio, fin);
});

const cambiarPagina = (pagina) => {
  if (pagina >= 1 && pagina <= totalPaginas.value) {
    paginacion.paginaActual = pagina;
  }
};

// --- ESTILOS DE BADGES ---
const getBadgeClass = (situacion) => {
  const base = 'status-badge shadow-sm ';
  if (!situacion) return base + 'status-disponible';
  const s = situacion.toLowerCase();
  if (s.includes('asignado')) return base + 'status-asignado';
  if (s.includes('baja')) return base + 'status-baja';
  if (s.includes('mantenimiento')) return base + 'status-mantenimiento';
  return base + 'status-disponible';
};
</script>

<template>
  <div class="container-fluid py-3 py-md-4 app-transition">
    <div class="row">
      <div class="col-lg-12">
        <div class="card card-custom border-0 shadow-lg border-radius-xl overflow-hidden">

          <div class="header-tamep d-flex flex-column flex-md-row justify-content-between align-items-center px-3 px-md-4 py-3 gap-3">
            <div class="d-flex flex-column flex-sm-row align-items-center text-center text-sm-start gap-2 gap-sm-0">
              <div class="logo-container-left shadow-sm me-sm-3 mb-2 mb-sm-0">
                <img :src="logo" alt="TAMEP Logo" class="logo-img">
              </div>
              <div class="border-sm-start ps-sm-3 border-white opacity-8">
                <h6 class="mb-0 text-white text-uppercase ls-1 font-weight-bolder">Gestión de Inventario Institucional</h6>
                <small class="text-white opacity-7">Control y Búsqueda Avanzada de Activos - TAMEP</small>
              </div>
            </div>
            <div class="text-center text-md-end text-white">
              <span class="badge bg-white text-azul-tamep px-3 py-2 font-weight-bolder shadow-sm" style="border-radius: 8px;">
                <i class="fas fa-boxes me-1"></i> {{ activos.length }} Activos Encontrados
              </span>
            </div>
          </div>

          <div class="card-body p-3 p-md-4 bg-section-custom">
            <!-- BUSCADOR E INTELIGENCIA DE FILTRADO -->
            <div class="card card-custom border border-custom-divider shadow-sm p-3 p-md-4 mb-4">
              <div class="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom border-custom-divider">
                <div class="d-flex align-items-center">
                  <div class="step-number me-2"><i class="fas fa-filter text-xs"></i></div>
                  <h6 class="font-weight-bolder mb-0 text-title-custom text-uppercase">Buscador Inteligente Avanzado</h6>
                </div>
                <button class="btn btn-sm btn-outline-secondary mb-0" @click="limpiarFiltros" title="Restablecer todos los filtros">
                  <i class="fas fa-eraser me-1"></i> Limpiar Filtros
                </button>
              </div>

              <!-- FILA 1: BUSQUEDA GENERAL + JERARQUIA ORGANIZACIONAL -->
              <div class="row g-2 mb-2">
                <div class="col-12 col-md-3">
                  <label class="label-custom">Búsqueda General</label>
                  <div class="input-group input-group-custom">
                    <span class="input-group-text bg-input-custom border-custom-divider"><i class="fas fa-search text-custom-muted"></i></span>
                    <input type="text" v-model="filtros.search" @keyup.enter="realizarBusqueda"
                      class="form-control input-custom border-start-0 ps-2" placeholder="Código, serie, nombre...">
                  </div>
                </div>

                <div class="col-12 col-md-3">
                  <label class="label-custom">Regional / Sede</label>
                  <select v-model="filtros.regional" class="form-select select-custom">
                    <option value="">Todas las regionales</option>
                    <option v-for="r in regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
                  </select>
                </div>

                <div class="col-12 col-md-3">
                  <label class="label-custom">Gerencia / Unidad</label>
                  <select v-model="filtros.gerencia" class="form-select select-custom" :disabled="!filtros.regional">
                    <option value="">{{ filtros.regional ? 'Todas las gerencias' : 'Requiere Regional' }}</option>
                    <option v-for="g in gerencias" :key="g.id" :value="g.id">{{ g.nombre }}</option>
                  </select>
                </div>

                <div class="col-12 col-md-3">
                  <label class="label-custom text-info">Sección / Área</label>
                  <select v-model="filtros.seccion" class="form-select select-custom border-info-light" :disabled="!filtros.gerencia">
                    <option value="">{{ filtros.gerencia ? 'Todas las secciones' : 'Requiere Gerencia' }}</option>
                    <option v-for="s in secciones" :key="s.id" :value="s.id">{{ s.nombre }}</option>
                  </select>
                </div>
              </div>

              <!-- FILA 2: CATEGORIA + ESTADOS Y GESTION -->
              <div class="row g-2 mb-2">
                <div class="col-12 col-md-3">
                  <label class="label-custom">Grupo Contable</label>
                  <select v-model="filtros.grupo_contable_seleccionado" class="form-select select-custom">
                    <option value="">Todos los grupos</option>
                    <option v-for="g in gruposContables" :key="g.id" :value="g.id">{{ g.nombre }}</option>
                  </select>
                </div>

                <div class="col-12 col-md-3">
                  <label class="label-custom">Tipo de Activo</label>
                  <select v-model="filtros.tipo_activo" class="form-select select-custom" :disabled="!tiposActivos.length">
                    <option value="">Todos los tipos</option>
                    <option v-for="t in tiposActivos" :key="t.id" :value="t.id">{{ t.nombre }}</option>
                  </select>
                </div>

                <div class="col-12 col-md-3">
                  <label class="label-custom">Situación de Gestión</label>
                  <select v-model="filtros.situacion_activo" @change="realizarBusqueda" class="form-select select-custom">
                    <option value="">Cualquier Situación</option>
                    <option value="Disponible">Disponible</option>
                    <option value="Asignado">Asignado</option>
                    <option value="Mantenimiento">En Mantenimiento</option>
                    <option value="Baja">Dado de Baja</option>
                  </select>
                </div>

                <div class="col-12 col-md-3">
                  <label class="label-custom">Estado Físico</label>
                  <select v-model="filtros.estado_actual" @change="realizarBusqueda" class="form-select select-custom">
                    <option value="">Cualquier Estado</option>
                    <option value="Bueno">Bueno</option>
                    <option value="Regular">Regular</option>
                    <option value="Malo">Malo</option>
                  </select>
                </div>
              </div>

              <!-- FILA 3: EXISTENCIA DE CODIGOS + ACCION BUSCAR -->
              <div class="row g-2 align-items-end">
                <div class="col-6 col-md-3">
                  <label class="label-custom">¿Tiene Cód. Actual?</label>
                  <select v-model="filtros.tiene_tamep" @change="realizarBusqueda" class="form-select select-custom">
                    <option value="">Todos</option>
                    <option value="true">Sí (Con Cód. Actual)</option>
                    <option value="false">No (Falta Cód. Actual)</option>
                  </select>
                </div>

                <div class="col-6 col-md-3">
                  <label class="label-custom">¿Tiene Cód. Histórico?</label>
                  <select v-model="filtros.tiene_tab" @change="realizarBusqueda" class="form-select select-custom">
                    <option value="">Todos</option>
                    <option value="true">Sí (Con Cód. Histórico)</option>
                    <option value="false">No (Falta Cód. Histórico)</option>
                  </select>
                </div>

                <div class="col-6 col-md-3">
                  <label class="label-custom">Items por página</label>
                  <select v-model="paginacion.itemsPorPagina" class="form-select select-custom">
                    <option :value="10">10 por página</option>
                    <option :value="25">25 por página</option>
                    <option :value="50">50 por página</option>
                    <option :value="100">100 por página</option>
                  </select>
                </div>

                <div class="col-6 col-md-3">
                  <button @click="realizarBusqueda" class="btn btn-azul-tamep text-white w-100 mb-0 font-weight-bolder shadow-sm">
                    <i v-if="cargando" class="fas fa-spinner fa-spin me-2"></i>
                    <span v-else><i class="fas fa-search me-2"></i>Buscar</span>
                  </button>
                </div>
              </div>

            </div>

            <!-- TABLA DE RESULTADOS -->
            <div class="card card-custom border border-custom-divider shadow-sm overflow-hidden">
              <div class="table-responsive min-vh-50 p-0">
                <table class="table align-items-center mb-0 table-hover">
                  <thead class="bg-gray-100">
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Activo / Codificación</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">Ubicación Organzacional</th>
                      <th class="d-none d-lg-table-cell text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">
                        <i class="fas fa-user-tie me-1"></i>Responsable
                      </th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">Situación / Estado</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">Ficha</th>
                      <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="cargando">
                      <td colspan="6" class="text-center py-5">
                        <div class="spinner-border text-primary" role="status"></div>
                        <p class="mt-2 text-sm font-weight-bold text-custom-muted text-uppercase">Procesando Consulta...</p>
                      </td>
                    </tr>
                    <tr v-else-if="activos.length === 0">
                      <td colspan="6" class="text-center py-5">
                        <i class="fas fa-search-minus fa-3x text-secondary opacity-4 mb-3"></i>
                        <h6 class="text-title-custom mb-1">No se encontraron activos</h6>
                        <p class="text-custom-muted text-xs">Intente ajustar los criterios de búsqueda o limpiar los filtros.</p>
                      </td>
                    </tr>

                    <tr v-for="a in activosPaginados" :key="a.id" class="border-bottom hover-row" v-else>
                      <td>
                        <div class="d-flex px-3 py-2">
                          <div class="d-flex flex-column justify-content-center">
                            <h6 class="mb-0 text-sm font-weight-bold text-title-custom">{{ a.nombre_especifico }}</h6>
                            <p class="text-xs font-weight-bold text-azul-tamep mb-0">
                              CÓD. ACTUAL: {{ a.codigo_tamep || 'S/C' }}
                            </p>
                            <span class="text-xxs text-custom-muted">CÓD. HISTÓRICO: {{ a.codigo_tab || 'Sin registrar' }} | S/N: {{ a.serie || 'N/A' }}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span class="text-xs font-weight-bold text-title-custom d-block">{{ a.seccion_nombre || a.gerencia_nombre || 'Sin Sección' }}</span>
                        <span class="text-xxs font-weight-bold text-custom-muted">
                          <i class="fas fa-map-marker-alt me-1 text-danger"></i>{{ a.regional_nombre || 'Sin Regional' }}
                        </span>
                      </td>
                      <td class="d-none d-lg-table-cell align-middle px-2">
                        <div v-if="a.custodio_nombre" class="d-flex align-items-center gap-2">
                          <div class="icon-box-badge bg-azul-tamep-light">
                            <i class="fas fa-user text-azul-tamep text-xxs"></i>
                          </div>
                          <div>
                            <span class="text-xs font-weight-bold text-title-custom d-block">{{ a.custodio_nombre }}</span>
                            <span class="text-xxs text-custom-muted">{{ a.cargo_nombre || 'Sin cargo' }}</span>
                          </div>
                        </div>
                        <span v-else class="text-xxs text-custom-muted fst-italic">
                          <i class="fas fa-warehouse me-1"></i>Sin Asignar
                        </span>
                      </td>
                      <td class="align-middle text-center">
                        <span :class="getBadgeClass(a.situacion_activo)">{{ a.situacion_activo }}</span>
                        <div class="mt-1">
                          <span class="text-xxs font-weight-bolder border rounded px-2 py-1"
                            :class="{ 'text-success border-success bg-success-light': a.estado_actual === 'Bueno', 'text-warning border-warning bg-warning-light': a.estado_actual === 'Regular', 'text-danger border-danger bg-danger-light': a.estado_actual === 'Malo' }">
                            <i class="fas fa-heartbeat me-1"></i>{{ a.estado_actual }}
                          </span>
                        </div>
                      </td>
                      <td class="align-middle text-center px-2">
                        <router-link :to="'/activo/detalle/' + a.id" class="btn btn-outline-dark btn-sm mb-0 px-2 btn-cancel-custom">
                          <i class="fas fa-file-pdf me-1"></i> Ficha
                        </router-link>
                      </td>
                      <td class="text-center align-middle px-2">
                        <router-link v-if="['admin_informatico', 'admin_principal', 'admin_regional', 'consultor'].includes(rolUsuario)" :to="'/activo/gestion/' + a.id"
                          class="btn btn-link text-azul-tamep p-0 mb-0 font-weight-bold me-2" title="Editar Activo">
                          <i class="fas fa-tools text-lg"></i>
                        </router-link>
                        <button v-if="puedeEliminar" @click="eliminarActivo(a.id, a.codigo_tamep || a.codigo_tab)" 
                          class="btn btn-link text-danger p-0 mb-0 font-weight-bold" title="Eliminar y Auditar">
                          <i class="fas fa-trash text-lg"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- CONTROLES DE PAGINACIÓN -->
              <div v-if="activos.length > 0 && !cargando"
                class="d-flex flex-column flex-md-row justify-content-between align-items-center px-3 px-md-4 py-3 border-top border-custom-divider bg-light bg-pagination-custom gap-3 text-center">
                <span class="text-xs font-weight-bold text-custom-muted">
                  Mostrando {{ (paginacion.paginaActual - 1) * paginacion.itemsPorPagina + 1 }} a
                  {{ Math.min(paginacion.paginaActual * paginacion.itemsPorPagina, activos.length) }}
                  de {{ activos.length }} activos
                </span>

                <div class="d-flex justify-content-center justify-content-md-end">
                  <ul class="pagination pagination-sm pagination-primary m-0 align-items-center">
                    <!-- Ir a Primera Página -->
                    <li class="page-item" :class="{ disabled: paginacion.paginaActual === 1 }">
                      <button class="page-link" @click="cambiarPagina(1)" title="Primera Página">
                        <i class="fas fa-angle-double-left"></i>
                      </button>
                    </li>
                    <!-- Página Anterior -->
                    <li class="page-item" :class="{ disabled: paginacion.paginaActual === 1 }">
                      <button class="page-link" @click="cambiarPagina(paginacion.paginaActual - 1)" title="Página Anterior">
                        <i class="fas fa-angle-left"></i>
                      </button>
                    </li>

                    <!-- Puntos suspensivos inicio -->
                    <li class="page-item disabled d-none d-sm-block" v-if="paginasVisibles.length && paginasVisibles[0] > 1">
                      <span class="page-link border-0">...</span>
                    </li>

                    <!-- Páginas Visibles en Ventana Compacta -->
                    <li class="page-item" v-for="pag in paginasVisibles" :key="pag"
                      :class="{ active: pag === paginacion.paginaActual }">
                      <button class="page-link font-weight-bold" @click="cambiarPagina(pag)">{{ pag }}</button>
                    </li>

                    <!-- Puntos suspensivos fin -->
                    <li class="page-item disabled d-none d-sm-block" v-if="paginasVisibles.length && paginasVisibles[paginasVisibles.length - 1] < totalPaginas">
                      <span class="page-link border-0">...</span>
                    </li>

                    <!-- Página Siguiente -->
                    <li class="page-item" :class="{ disabled: paginacion.paginaActual === totalPaginas }">
                      <button class="page-link" @click="cambiarPagina(paginacion.paginaActual + 1)" title="Página Siguiente">
                        <i class="fas fa-angle-right"></i>
                      </button>
                    </li>

                    <!-- Ir a Última Página -->
                    <li class="page-item" :class="{ disabled: paginacion.paginaActual === totalPaginas }">
                      <button class="page-link" @click="cambiarPagina(totalPaginas)" :title="`Última Página (${totalPaginas})`">
                        <i class="fas fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.app-transition { transition: all 0.3s ease; }
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.logo-container-left { background: white; padding: 5px 12px; border-radius: 8px; }
.logo-img { max-height: 35px; }

.label-custom {
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 4px;
  display: block;
}

.input-custom, .select-custom {
  background-color: #ffffff;
  color: #1e293b;
  border-color: #cbd5e1;
  font-size: 0.82rem;
}
.input-custom:focus, .select-custom:focus {
  border-color: #003366;
  box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.15);
}

.bg-input-custom { background-color: #f8fafc; color: #64748b; }
.border-info-light { border-color: #38bdf8 !important; }

.step-number {
  background: #003366;
  color: white;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-custom {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
}

.text-title-custom { color: #1e293b; }
.text-custom-muted { color: #64748b; }
.border-custom-divider { border-color: #e2e8f0 !important; }
.bg-section-custom { background-color: #f8fafc; }

.btn-azul-tamep {
  background-color: #003366;
  border-color: #003366;
  color: #ffffff;
}
.btn-azul-tamep:hover {
  background-color: #002244;
  color: #ffffff;
}

.text-azul-tamep { color: #003366; }
.bg-azul-tamep-light { background-color: #e2e8f0; }

.icon-box-badge {
  width: 28px; height: 28px; min-width: 28px; min-height: 28px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%; flex-shrink: 0;
}

.input-group-custom .input-group-text {
  display: flex; align-items: center; justify-content: center;
  min-width: 38px; padding: 0.4rem 0.6rem;
}

.min-vh-50 { min-height: 50vh; }

/* BADGES DE GESTIÓN */
.status-badge {
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  border: 1px solid transparent;
}
.status-asignado { background-color: #d1e7dd; color: #0f5132; border-color: #badbcc; }
.status-disponible { background-color: #cfe2ff; color: #084298; border-color: #b6d4fe; }
.status-mantenimiento { background-color: #fff3cd; color: #856404; border-color: #ffeeba; }
.status-baja { background-color: #f8d7da; color: #842029; border-color: #f5c6cb; }

.bg-success-light { background-color: #e8f5e9; }
.bg-warning-light { background-color: #fff8e1; }
.bg-danger-light { background-color: #ffebee; }

.btn-cancel-custom { background-color: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }
.btn-cancel-custom:hover { background-color: #e2e8f0; color: #1e293b; }
</style>

<style>
/* =========================================
   SOPORTE COMPLETO PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom, .dark-mode .card-custom {
  background-color: #1e293b !important;
  border: 1px solid #334155 !important;
}
.dark-version .bg-section-custom, .dark-mode .bg-section-custom {
  background-color: #090d16 !important;
}
.dark-version .bg-pagination-custom, .dark-mode .bg-pagination-custom {
  background-color: #0f172a !important;
  border-top: 1px solid #334155 !important;
}
.dark-version .border-custom-divider, .dark-mode .border-custom-divider {
  border-color: #334155 !important;
}
.dark-version .text-title-custom, .dark-mode .text-title-custom {
  color: #ffffff !important;
}
.dark-version .text-custom-muted, .dark-mode .text-custom-muted {
  color: #cbd5e1 !important;
}
.dark-version .hover-row:hover, .dark-mode .hover-row:hover {
  background-color: #0f172a !important;
}

/* Tabla en Modo Oscuro */
.dark-version .table th, .dark-mode .table th {
  color: #cbd5e1 !important;
  background-color: #0f172a !important;
  border-bottom: 1px solid #334155 !important;
}
.dark-version .table td, .dark-mode .table td {
  border-bottom: 1px solid #334155 !important;
}

/* Selects, Inputs y Options en Modo Oscuro */
.dark-version .input-custom, .dark-mode .input-custom,
.dark-version .select-custom, .dark-mode .select-custom {
  background-color: #0f172a !important;
  color: #f8fafc !important;
  border-color: #334155 !important;
}
.dark-version .bg-input-custom, .dark-mode .bg-input-custom {
  background-color: #1e293b !important;
  color: #94a3b8 !important;
  border-color: #334155 !important;
}
.dark-version select option, .dark-mode select option {
  background-color: #0f172a !important;
  color: #f8fafc !important;
}
.dark-version select:disabled, .dark-mode select:disabled {
  background-color: #1e293b !important;
  color: #64748b !important;
  border-color: #334155 !important;
  opacity: 0.7;
}

/* Badges en modo oscuro */
.dark-version .bg-azul-tamep-light, .dark-mode .bg-azul-tamep-light {
  background-color: #334155 !important;
}
.dark-version .bg-azul-tamep-light i, .dark-mode .bg-azul-tamep-light i {
  color: #38bdf8 !important;
}
</style>